import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import StepperCDS from '@sharedCR/coreDesignSystem/StepperCDS';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import { decideDrawRequestAction } from 'store/construction/actions';
import { selectConstructionBudgetLines, selectDrawRequestById } from 'store/construction/selectors';
import { DrawReviewDecision } from 'store/construction/types';
import { openConfirmationDialogAction } from 'store/global/actions';

import InlineNotice from '../components/InlineNotice';
import { DRAW_LIFECYCLE_STEPS } from '../Construction.constants';
import { getDrawStepperActiveStatus } from '../Construction.utils';

import DrawChecklistSection from './components/DrawChecklistSection';
import DrawDocumentsSection from './components/DrawDocumentsSection';
import DrawLineItemsSection from './components/DrawLineItemsSection';
import DrawSummarySection from './components/DrawSummarySection';
import { DRAW_REVIEW_NOTES_FIELD_NAME } from './DrawReviewDrawer.constants';
import {
  drawerBodyStyles,
  footerActionsStyles,
  footerSpacerStyles,
  sectionStyles,
  sectionTitleStyles,
} from './DrawReviewDrawer.styles';
import { DrawReviewDrawerTestIds } from './DrawReviewDrawer.testIds';
import {
  enrichDrawLineItems,
  getBlockingChecklistItem,
  getDecisionConfirmation,
  getDrawerTitle,
} from './DrawReviewDrawer.utils';

const EMPTY_LIST = [];

const DrawReviewDrawer = ({
  applicationId,
  drawRequestId,
  isEditAllowed,
  open,
  onClose,
  onDecided,
}) => {
  const dispatch = useDispatch();

  const selectDrawRequest = useMemo(
    () => selectDrawRequestById({ applicationId, drawRequestId }),
    [applicationId, drawRequestId],
  );
  const drawRequest = useSelector(selectDrawRequest);
  const budgetLines = useSelector(selectConstructionBudgetLines(applicationId));

  const lineItems = useMemo(
    () => enrichDrawLineItems(drawRequest?.lineItems ?? EMPTY_LIST, budgetLines),
    [drawRequest, budgetLines],
  );

  const [note, setNote] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const blockingItem = useMemo(
    () => getBlockingChecklistItem(drawRequest?.checklistItems ?? EMPTY_LIST),
    [drawRequest],
  );

  const isDirty = !!note || !!selectedFile;

  const onNoteChange = useCallback(event => setNote(event.target.value), []);

  const onCloseDrawer = useCallback(() => {
    setNote('');
    setSelectedFile(null);
    setErrorMessage('');
    onClose();
  }, [onClose]);

  const submitDecision = useCallback(
    decision => {
      setIsLoading(true);

      return dispatch(
        decideDrawRequestAction({
          applicationId,
          drawRequestId,
          payload: { decision, notes: note },
        }),
      )
        .then(() => {
          // A decision mutates the budget projection (funded / retainage / available) and the draw
          // list, but the decision action only patches the single draw. Refetch so the summary stat
          // cards and draw list reflect the new figures without a full page reload (DEF-3).
          onDecided?.();
          onCloseDrawer();
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, drawRequestId, note, onDecided, onCloseDrawer],
  );

  const onApprove = useCallback(() => submitDecision(DrawReviewDecision.APPROVE), [submitDecision]);

  const onConfirmDecision = useCallback(
    decision => {
      const { message, description, onAcceptButtonName } = getDecisionConfirmation({
        decision,
        drawRequest,
      });

      dispatch(
        openConfirmationDialogAction({
          message,
          description,
          onAcceptButtonName,
          onAccept: () => submitDecision(decision),
        }),
      );
    },
    [dispatch, drawRequest, submitDecision],
  );

  const onDeny = useCallback(() => onConfirmDecision(DrawReviewDecision.DENY), [onConfirmDecision]);
  const onRequestChanges = useCallback(
    () => onConfirmDecision(DrawReviewDecision.REQUEST_CHANGES),
    [onConfirmDecision],
  );

  const content = useMemo(() => {
    if (!drawRequest) {
      return null;
    }

    return (
      <Box data-testid={DrawReviewDrawerTestIds.DRAWER} sx={drawerBodyStyles}>
        <Box sx={sectionStyles}>
          <Typography sx={sectionTitleStyles} variant="bold12">
            Status
          </Typography>
          <StepperCDS
            activeStep={getDrawStepperActiveStatus(drawRequest.status)}
            steps={DRAW_LIFECYCLE_STEPS}
          />
        </Box>
        <DrawSummarySection drawRequest={drawRequest} />
        <DrawLineItemsSection lineItems={lineItems} />
        <DrawChecklistSection checklistItems={drawRequest.checklistItems ?? EMPTY_LIST} />
        <DrawDocumentsSection
          documents={drawRequest.documents ?? EMPTY_LIST}
          errorMessage={errorMessage}
          selectedFile={selectedFile}
          onError={setErrorMessage}
          onSelectFile={setSelectedFile}
        />
        <Box sx={sectionStyles}>
          <Typography sx={sectionTitleStyles} variant="bold12">
            Review Notes
          </Typography>
          <TextFieldCDS
            multiline
            dataTestId={DrawReviewDrawerTestIds.NOTES_FIELD}
            label="Internal note (optional)"
            name={DRAW_REVIEW_NOTES_FIELD_NAME}
            placeholder="Add a note for the disbursement team…"
            rows={3}
            value={note}
            onChange={onNoteChange}
          />
        </Box>
        {blockingItem && (
          <InlineNotice
            dataTestId={DrawReviewDrawerTestIds.APPROVAL_BLOCKED_NOTE}
            variant={StatusEnum.WARNING}
          >
            {'Approve is unavailable — the '}
            <strong>{blockingItem.name}</strong>
            {
              ' checklist item is still pending. Clear it (or override with a documented reason) to enable disbursement.'
            }
          </InlineNotice>
        )}
      </Box>
    );
  }, [drawRequest, lineItems, errorMessage, selectedFile, note, onNoteChange, blockingItem]);

  const actions = useMemo(
    () => (
      <>
        <Button
          data-testid={DrawReviewDrawerTestIds.REQUEST_CHANGES_BUTTON}
          disabled={!isEditAllowed || isLoading}
          variant="text"
          onClick={onRequestChanges}
        >
          Request Changes
        </Button>
        <Box sx={footerSpacerStyles} />
        <Box sx={footerActionsStyles}>
          <Button
            color="error"
            data-testid={DrawReviewDrawerTestIds.DENY_BUTTON}
            disabled={!isEditAllowed || isLoading}
            variant="outlined"
            onClick={onDeny}
          >
            Deny
          </Button>
          <Button
            data-testid={DrawReviewDrawerTestIds.APPROVE_BUTTON}
            disabled={!isEditAllowed || isLoading || !!blockingItem}
            variant="contained"
            onClick={onApprove}
          >
            Approve Draw
          </Button>
        </Box>
      </>
    ),
    [isEditAllowed, isLoading, blockingItem, onRequestChanges, onDeny, onApprove],
  );

  if (!drawRequest) {
    return null;
  }

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={DrawReviewDrawerTestIds.DRAWER_CONTAINER}
      isDirty={isDirty}
      isLoading={isLoading}
      open={open}
      size={DrawerSize.MEDIUM}
      title={getDrawerTitle(drawRequest)}
      onClose={onCloseDrawer}
    />
  );
};

DrawReviewDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  applicationId: PropTypes.string,
  drawRequestId: PropTypes.string,
  isEditAllowed: PropTypes.bool,
  open: PropTypes.bool,
  onDecided: PropTypes.func,
};

export default memo(DrawReviewDrawer);
